/**
 * CDN utilities for handling image URLs
 */

const CDN_BASE_URL = 'https://10582abd9fe.vws.vegacdn.vn'
const FALLBACK_BASE_URL = 'https://okapi999.com'
const DEFAULT_TEAM_LOGO = 'https://okapi999.com/images/teams/default.png'
const DEFAULT_LEAGUE_LOGO = 'https://okapi999.com/images/leagues/default.png'
const DEFAULT_CASTER_AVATAR = 'https://70303d714.vws.vegacdn.vn/cakhiastatic/avtblv/avatar-default.png'

/**
 * Generates team logo URL with CDN prefix (Priority: CDN first)
 * @param avatar - Team avatar path from database
 * @param fallbackUrl - Fallback URL if avatar is empty
 * @returns Full CDN URL for team logo
 */
export function getTeamLogoUrl(avatar?: string, fallbackUrl?: string): string {
  // If avatar is empty or null, use default fallback
  if (!avatar) {
    return fallbackUrl || DEFAULT_TEAM_LOGO
  }

  // If already a full URL (starts with http), return as is
  if (avatar.startsWith('https')) {
    return avatar
  }

  // PRIORITY: Always try CDN first for relative paths
  // Remove leading slash if present to avoid double slashes
  const cleanPath = avatar.startsWith('/') ? avatar.slice(1) : avatar
  return `${CDN_BASE_URL}/${cleanPath}`
}

/**
 * Generates team logo URL with error fallback for img onError
 * Priority: CDN first, then okapi999.com if CDN fails
 * @param avatar - Team avatar path from database  
 * @param fallbackUrl - Fallback URL if avatar fails to load
 * @returns Primary CDN URL and fallback okapi999 URL
 */
export function getTeamLogoUrlWithFallback(avatar?: string, fallbackUrl?: string): {
  src: string
  fallback: string
} {
  // If no avatar, use default immediately
  if (!avatar) {
    const defaultUrl = fallbackUrl || DEFAULT_TEAM_LOGO
    return { src: defaultUrl, fallback: defaultUrl }
  }

  // If already a full URL, use it as primary
  if (avatar.startsWith('http')) {
    return { 
      src: avatar, 
      fallback: fallbackUrl || DEFAULT_TEAM_LOGO 
    }
  }

  // PRIMARY: Try CDN first
  const cleanPath = avatar.startsWith('/') ? avatar.slice(1) : avatar
  const cdnUrl = `${CDN_BASE_URL}/${cleanPath}`
  
  // FALLBACK: okapi999.com if CDN fails
  const okapi999Url = avatar.startsWith('/') 
    ? `${FALLBACK_BASE_URL}${avatar}` 
    : `${FALLBACK_BASE_URL}/${avatar}`
  
  return { 
    src: cdnUrl, 
    fallback: fallbackUrl || okapi999Url 
  }
}

/**
 * Generates caster avatar URL with CDN prefix (Priority: CDN first)
 * @param avatar - Caster avatar path from database
 * @param fallbackUrl - Fallback URL if avatar is empty
 * @returns Object with src (CDN URL) and fallback URL
 */
export function getCasterAvatarUrl(avatar?: string): string {
  if (!avatar) {
    return DEFAULT_CASTER_AVATAR
  }
  
  if (avatar.startsWith('http')) {
    return avatar
  }
  
  const cleanPath = avatar.startsWith('/') ? avatar.slice(1) : avatar
  return `${CDN_BASE_URL}/${cleanPath}`
}

/**
 * Generates caster avatar URL with CDN prefix and fallback logic
 * @param avatar - Caster avatar path from database
 * @param fallbackUrl - Custom fallback URL if avatar is empty
 * @returns Object with src (CDN URL) and fallback URL
 */
export function getCasterAvatarUrlWithFallback(avatar?: string, fallbackUrl?: string): {
  src: string
  fallback: string
} {
  if (!avatar) {
    const defaultUrl = fallbackUrl || DEFAULT_CASTER_AVATAR
    return { src: defaultUrl, fallback: defaultUrl }
  }
  
  if (avatar.startsWith('https')) {
    return { src: avatar, fallback: fallbackUrl || DEFAULT_CASTER_AVATAR }
  }
  
  const cleanPath = avatar.startsWith('/') ? avatar.slice(1) : avatar
  const cdnUrl = `${CDN_BASE_URL}/${cleanPath}`
  const okapi999Url = avatar.startsWith('/')
    ? `${FALLBACK_BASE_URL}${avatar}`
    : `${FALLBACK_BASE_URL}/${avatar}`
  
  return { src: cdnUrl, fallback: fallbackUrl || okapi999Url }
}
